import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface FilterChipOption {
  value: string;
  label: string;
  count?: number;
}

export interface FilterChipsProps {
  options: FilterChipOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  /** Chip whose value this matches shows a spinner (a save is in flight). */
  loadingValue?: string;
  /** Disables every chip — e.g. while a change is saving. */
  disabled?: boolean;
}

/**
 * A row of pill-style toggle chips for single-select filtering.
 * Sits next to the search bar in place of a Select dropdown.
 */
export function FilterChips({
  options,
  value,
  onChange,
  className,
  loadingValue,
  disabled,
}: FilterChipsProps) {
  return (
    <div
      className={cn(
        'flex max-w-full flex-nowrap items-center gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] sm:flex-wrap sm:overflow-visible sm:pb-0 [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        const loading = loadingValue === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            disabled={disabled}
            className={cn(
              'inline-flex min-h-8 shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors sm:min-h-0',
              active
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            {loading && <Loader2 className="h-3 w-3 animate-spin" />}
            {opt.label}
            {opt.count != null && (
              <span
                className={cn(
                  'rounded-full px-1 text-[10px] tabular-nums',
                  active ? 'bg-primary-foreground/20' : 'bg-muted-foreground/15',
                )}
              >
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
